import { useRef, useEffect } from "react";
import { createMarkerChild } from "../../helpers";
import { useLocation } from "react-router-dom";
import { RouteConstants } from "../../constants";
import { useRecoilValue } from "recoil";
import { selectedTabState, geoState } from "../../states";
import { GeoId } from "../../types/geo.types";

interface PoIMarkersProps {
	type: string;
	map: google.maps.Map | null;
	pois: any;
	handlePopupHover: (type: string, branch?: any, focusedBrand?: any) => void;
	isSubDistrict: boolean;
	focusedPoi?: React.MutableRefObject<any>;
	handleClick: (type: string | null, data: any) => void;
	resetZIndex?: boolean;
	currGeoCode: GeoId;
}

export const HoverMarkers: React.FC<PoIMarkersProps> = ({ type, map, pois, handlePopupHover, isSubDistrict, focusedPoi, handleClick, resetZIndex = false, currGeoCode }) => {
	const { pathname } = useLocation();
	const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
	const listenersRef = useRef<google.maps.MapsEventListener[]>([]);
	const selectedTab = useRecoilValue(selectedTabState);
	const selectedGeo = useRecoilValue(geoState);
	const baseZIndices = useRef<Map<any, number>>(new Map());

	// Effect to reset all marker z-indices when geo hover popup appears
	// This ensures markers don't stay at high z-index and overlap the popup
	useEffect(() => {
		if (resetZIndex) {
			markersRef.current.forEach(marker => {
				const baseZ = baseZIndices.current.get(marker) ?? 150;
				if (marker.zIndex && marker.zIndex > 200) {
					marker.zIndex = baseZ;
				}
			});
		}
	}, [resetZIndex]);
	
	useEffect(() => {
		if (!map || !window.google?.maps?.marker?.AdvancedMarkerElement) return;

		listenersRef.current.forEach(listener => window.google.maps.event.removeListener(listener));
		listenersRef.current = [];

		markersRef.current.forEach(marker => marker.map = null);
		markersRef.current = [];
		baseZIndices.current.clear();

		const updatedPoIs = (currGeoCode == selectedGeo.district && selectedTab === 4 && type === 'mbp') ? (pois ?? []).filter((poi: any) => !poi.overlap) : pois;

		const newPoIMarkers = updatedPoIs?.map((poi: any) => {
			const marker = createMarkerChild(type, poi, map, isSubDistrict, pathname);
			const baseZ: any = marker.zIndex ?? 150; // remember original
			baseZIndices.current.set(marker, baseZ);

			if (pathname === RouteConstants.root) {
				const handleMouseOver = () => {
					if (focusedPoi?.current?.type) return;
					if (!selectedGeo?.village) {
						// Reset ALL other markers to base z-index first
						markersRef.current.forEach(m => {
							if (m !== marker) {
								const origZ = baseZIndices.current.get(m) ?? 150;
								m.zIndex = origZ;
							}
						});
						// Then bring this one to top
						marker.zIndex = 999;
					}
					handlePopupHover(type, type === "poi" ? poi.data : poi, focusedPoi?.current);
				};

				const handleMouseOut = () => {
					if (selectedGeo?.village || focusedPoi?.current?.type) return;
					marker.zIndex = baseZ; // reset
					handlePopupHover(type);
				};

				const mouseOverListener: any = marker.addEventListener("mouseover", handleMouseOver);
				const mouseOutListener: any = marker.addEventListener("mouseout", handleMouseOut);

				listenersRef.current.push(mouseOverListener, mouseOutListener);
				if (selectedGeo.state) {
					const clickListener: any = marker.addListener("click", () => {
						// Allow clicks even when there's a focused item, to enable switching between markers
						handleClick(type, poi);
					});
					listenersRef.current.push(clickListener);
				}
			}

			// Hide marker if it's the focused one (being shown in popup)
			// OR reset z-index if there's any focused item to ensure it doesn't overlap the popup
			if (focusedPoi?.current?.type === type &&
			    focusedPoi?.current?.data?.latitude === poi.latitude &&
			    focusedPoi?.current?.data?.longitude === poi.longitude) {
				if (marker.content && marker.content instanceof HTMLElement) {
					marker.content.style.display = 'none';
				}
			} else if (focusedPoi?.current?.type && marker.zIndex && marker.zIndex > 200) {
				// Reset z-index to base if any item is focused, to ensure markers stay behind popup
				marker.zIndex = baseZ;
			}

			return marker;
		});

		markersRef.current = newPoIMarkers;

		// Cleanup on unmount or update
		return () => {
			markersRef.current.forEach(marker => marker.map = null);
			listenersRef.current.forEach(listener => window.google.maps.event.removeListener(listener));
			listenersRef.current = [];
		};
	}, [map, pois, selectedTab]);

	return null;
};